import { useRef } from 'react';
import { MARKETERS_APPLY_ID, marketersOfferModal as content } from '~/content/marketers';
import { ModalShell } from '~/components/layout';
import { Button, Icon, ResponsiveText } from '~/components/ui';
import { useContent } from '~/lib/useLanguage';

/**
 * "Learn more" dialog for the marketer cashback offer (Figma 1117:18853 desktop,
 * 1119:19113 mobile), in the shared QR-modal chrome (ModalShell: blur backdrop,
 * rising card, cursor-following close, Escape, scroll lock and focus handling)
 * and the same card treatment as PayrollOfferModal.
 */
export function MarketersOfferModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { ui } = useContent();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const goToApplication = () => {
    onClose();
    requestAnimationFrame(() => {
      const form = document.getElementById(MARKETERS_APPLY_ID);
      form?.scrollIntoView({ behavior: 'smooth' });
      // Move focus with the viewport so keyboard and screen-reader users land on the form.
      form?.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy="marketers-offer-title"
      initialFocusRef={closeButtonRef}
      className="w-full max-w-[750px]">
      {/* Tall content: the card scrolls inside itself rather than the page. */}
      <div className="flex max-h-[80dvh] flex-col overflow-y-auto overscroll-contain rounded-[32px] bg-white px-6 pt-6 pb-8 text-grey-1000 sm:max-h-[90dvh] md:px-8 md:pt-8">
        <button
          ref={closeButtonRef}
          type="button"
          aria-label={ui.close}
          onClick={onClose}
          className="flex size-6 cursor-pointer items-center justify-center self-end">
          <Icon name="close" />
        </button>

        <div className="mt-10 border-b border-black-6 pb-8">
          <h2
            id="marketers-offer-title"
            className="max-w-[329px] text-h3 leading-[1.16] font-semibold tracking-[-0.704px] md:max-w-[511px] md:text-h2 md:tracking-[-0.88px]">
            {content.title}
          </h2>
        </div>
        <p className="mt-[23px] text-[15px] leading-[1.32] tracking-[0.27px] text-black md:max-w-[686px] md:text-body">
          {content.body}
        </p>

        <h3 className="mt-10 py-2 text-[15px] leading-[1.32] font-semibold tracking-[0.27px] md:text-body md:leading-[1.16]">
          {content.howItWorksTitle}
        </h3>
        <ol className="mt-[17px] flex flex-col gap-2 border-b border-black-6 pb-7">
          {content.steps.map((step) => (
            <li key={step.text} className="flex items-center gap-5">
              <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-black-6">
                <Icon name={step.icon} className="size-5" />
              </span>
              <span className="py-2 text-[15px] leading-[1.32] tracking-[0.27px] md:text-body">
                <ResponsiveText text={step.text} mobileText={step.mobileText} />
              </span>
            </li>
          ))}
        </ol>

        <p className="mt-10 text-[15px] leading-[1.32] tracking-[0.27px] md:text-body">
          <span className="block font-semibold">{content.noCardTitle}</span>
          {content.noCardBody}
        </p>
        <Button
          onClick={goToApplication}
          className="mt-10 self-start !text-[14px] !tracking-[0.168px]">
          {content.cta}
        </Button>
        <p className="mt-6 text-[14px] leading-[1.32] tracking-[0.252px] text-grey-500">
          {content.terms}
        </p>
      </div>
    </ModalShell>
  );
}
